import { styled, alpha } from '@mui/material/styles'
import { Paper, Typography, Button, Box, Chip, IconButton } from '@mui/material'

export const RoomCardContainer = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  borderRadius: 12,
  padding: '20px',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 280,
  height: '100%',
  transition: 'all 0.2s ease-in-out',
  border: `1px solid ${theme.palette.divider}`,
  position: 'relative',
  overflow: 'hidden',
  boxShadow: theme.shadows[1],
  backgroundImage: 'none',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
    borderColor: alpha(theme.palette.primary.main, 0.35),
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
    opacity: 0,
    transition: 'opacity 0.2s ease-in-out',
  },
  '&:hover::before': {
    opacity: 1,
  },
}))

export const RoomHeader = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: 16,
  gap: 8,
  flexShrink: 0,
}))

export const RoomTypeIcon = styled(Box)(({ theme }) => ({
  color: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    fontSize: '1.2rem',
  },
}))

export const RoomTitleRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
}))

export const PlayerCountBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 10px',
  background: alpha(theme.palette.primary.main, 0.1),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
  borderRadius: 12,
  fontSize: '0.8rem',
  fontWeight: 600,
  color: theme.palette.primary.main,
  alignSelf: 'flex-start',
  '& svg': {
    fontSize: '0.9rem',
  },
}))

export const RoomContent = styled(Box)(() => ({
  flex: 1,
  marginBottom: 12,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 80,
}))

export const RoomTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  fontSize: '1.1rem',
  lineHeight: 1.3,
}))

export const RoomDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.9rem',
  marginBottom: 16,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minHeight: 40,
  lineHeight: 1.4,
  flexShrink: 0,
}))

export const RoomFooter = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  marginTop: 'auto',
  paddingTop: 16,
  gap: 12,
  flexShrink: 0,
}))

export const MembersChip = styled(Chip)(({ theme }) => ({
  background: alpha(theme.palette.primary.main, 0.12),
  color: theme.palette.primary.main,
  fontSize: '0.8rem',
  height: 28,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.28)}`,
  '& .MuiChip-icon': {
    color: theme.palette.primary.main,
    fontSize: '0.9rem',
    marginLeft: 4,
  },
  '& .MuiChip-label': {
    paddingLeft: 8,
    paddingRight: 8,
    fontWeight: 500,
  },
}))

export const ActionButtonsGroup = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
}))

export const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  textTransform: 'none',
  fontWeight: 600,
  padding: '8px 16px',
  backgroundColor: theme.palette.primary.main,
  fontSize: '0.85rem',
  minWidth: 70,
  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.35)}`,
  transition: 'all 0.2s ease-in-out',
  color: theme.palette.primary.contrastText,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.45)}`,
    transform: 'translateY(-1px)',
  },
  '&:disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
    boxShadow: 'none',
    transform: 'none',
  },
}))

export const ActionButtons = styled(Box)(() => ({
  display: 'flex',
  gap: 6,
  position: 'absolute',
  top: 12,
  right: 12,
}))

export const ActionIconButton = styled(IconButton)(({ theme }) => ({
  background: theme.palette.background.paper,
  padding: 6,
  width: 32,
  height: 32,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[1],
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    background: theme.palette.action.hover,
    transform: 'scale(1.05)',
  },
  '& svg': {
    fontSize: '1rem',
    color: theme.palette.text.secondary,
  },
  '&.edit:hover': {
    background: alpha(theme.palette.primary.main, 0.12),
    borderColor: theme.palette.primary.main,
    '& svg': {
      color: theme.palette.primary.main,
    },
  },
  '&.delete:hover': {
    background: alpha(theme.palette.error.main, 0.12),
    borderColor: theme.palette.error.main,
    '& svg': {
      color: theme.palette.error.main,
    },
  },
}))

export const CreatorInfo = styled(Typography)(({ theme }) => ({
  display: 'block',
  color: theme.palette.text.disabled,
  marginBottom: 8,
  fontSize: '0.8rem',
}))

export const StatusBadge = styled(Box)<{ status: 'online' | 'offline' | 'busy' }>(({ theme, status }) => {
  const success = theme.palette.success.main
  const warning = theme.palette.warning.main
  const muted = theme.palette.text.disabled
  return {
    position: 'absolute',
    top: 16,
    left: 16,
    display: 'flex',
    alignItems: 'center',
    padding: '4px 8px',
    borderRadius: 12,
    fontSize: '0.75rem',
    fontWeight: 600,
    background:
      status === 'online'
        ? alpha(success, 0.18)
        : status === 'busy'
          ? alpha(warning, 0.18)
          : alpha(theme.palette.text.primary, 0.08),
    color: status === 'online' ? success : status === 'busy' ? warning : muted,
    border: `1px solid ${
      status === 'online'
        ? alpha(success, 0.35)
        : status === 'busy'
          ? alpha(warning, 0.35)
          : alpha(theme.palette.text.primary, 0.15)
    }`,
    '&::before': {
      content: '""',
      display: 'inline-block',
      width: 6,
      height: 6,
      borderRadius: '50%',
      marginRight: 6,
      backgroundColor: status === 'online' ? success : status === 'busy' ? warning : muted,
    },
  }
})
